import React from "react";
import { Badge, Row, Col, Input, Button, FormGroup } from "reactstrap";
import "../../../assets/css/admin.css";
import { formatDate } from "../../../utils/date.js";
import "utils/jsdocTypes";
import { SAText } from "utils/functions";
import { SATextAreaInput } from "utils/functions";
import { SADropdown } from "utils/functions";
import { SADisputeForm } from "utils/functions";
import { SAFilesAndCopySections } from "utils/functions";
import { SAConfirm } from "utils/functions";

/**
 * @typedef {Object} Props
 * @property {AdminTransactionDetails} transaction
 * @property {Fn} resendTransactionReceipt
 * @property {UseReactToPrintFn} handlePrintInvoice
 * @property {UseReactToPrintFn} handlePrintReceipt
 */

/**
 * @function TransactionCard
 * @param {Props} props
 * @type {React.JSX.Element}
 */
const TransactionCard = ({
  transaction,
  setTransaction,
  resendTransactionReceipt,
  handlePrintInvoiceFn,
  handlePrintReceiptFn,
  disputeTransaction,
  updateDisputeValid,
  updateDisputeStatus,
  updateDisputeNotes,
  isInvoiceCardLoaded,
  isReceiptCardLoaded,
  customer,
  downloadInvoicePDF,
  downloadReceiptPDF,
}) => {
  return (
    <>
      <Row>
        <Col lg="4" md="4">
          <h5 className="section-heading">Transaction Information</h5>
        </Col>

        <Col lg="8" md="8" style={{ textAlign: "right" }}>
          {!transaction.disputed && (
            <Button
              color="success"
              className="toggle-status-btn"
              onClick={() => {
                SADisputeForm().then((res) => {
                  if (!res) {
                    return;
                  }

                  disputeTransaction({
                    variables: {
                      ctransid: transaction.ctransid,
                      disputevalid: res.disputeValid,
                      disputestatus: res.disputeStatus,
                      disputereason: res.disputeReason,
                      disputenotes: res.disputeNotes,
                      datetimedisputed: res.dateTimeDisputed,
                    },
                  }).then((res) => {
                    const status =
                      res?.data?.adminDisputeTransactionByCTransactionId
                        ?.status;

                    if (status) {
                      const data =
                        res.data.adminDisputeTransactionByCTransactionId
                          .transaction;
                      setTransaction((prevState) => {
                        return {
                          ...prevState,
                          disputed: data.disputed,
                          disputevalid: data.disputevalid,
                          disputestatus: data.disputestatus,
                          disputereason: data.disputereason,
                          disputenotes: data.disputenotes,
                          datetimedisputed: data.datetimedisputed,
                          disputeinitiatedby: data.disputeinitiatedby,
                        };
                      });
                    }
                  });
                });
              }}
            >
              Dispute
            </Button>
          )}

          {transaction.disputed && (
            <Button
              color="warning"
              className="toggle-status-btn"
              // disabled={!isInvoiceCardLoaded}
              onClick={() =>
                SAFilesAndCopySections(
                  [
                    {
                      id: "invoice",
                      name: "Invoice",
                      url: "/downloads/invoice.pdf",
                    },
                    {
                      id: "receipt",
                      name: "Receipt",
                      url: "/downloads/report.csv",
                    },
                  ],
                  [
                    {
                      title: "Product Description",
                      content: "Highly Encrypted Cloud Storage Volume",
                    },
                    {
                      title: "KYC Process",
                      content:
                        "We KYC all of our customers utilizing Plaid KYC.",
                    },
                    {
                      title: "Customer Information",
                      content:
                        "Name: " +
                        customer.firstname +
                        " " +
                        customer.lastname +
                        "\nAddress: " +
                        customer.address1 +
                        " " +
                        customer.address2 +
                        " " +
                        customer.city +
                        " " +
                        customer.state +
                        " " +
                        customer.zip +
                        "\nPhone: " +
                        customer.phone +
                        "\nEmail: " +
                        customer.email,
                    },
                  ],
                  {
                    downloadInvoicePDF: downloadInvoicePDF,
                    downloadReceiptPDF: downloadReceiptPDF,
                  }
                )
              }
            >
              Dispute Info
            </Button>
          )}

          <Button
            color="success"
            className="toggle-status-btn"
            disabled={!isInvoiceCardLoaded}
            onClick={() => handlePrintInvoiceFn()}
          >
            Print Invoice
          </Button>

          <Button
            color={"success"}
            className="toggle-status-btn"
            disabled={!isReceiptCardLoaded}
            onClick={() => handlePrintReceiptFn()}
          >
            Print Receipt
          </Button>

          <Button
            color="success"
            className="toggle-status-btn"
            onClick={() =>
              SAConfirm(
                "Resend Receipt?",
                "This will resend the transaction receipt email to the customer.",
                () => {
                  resendTransactionReceipt({
                    variables: { transactionid: transaction.ctransid },
                  }).then((res) => {
                    if (res.data.adminResendTransactionReceipt.status) {
                      SAText("Success", "Email sent successfully", "success");
                    } else {
                      SAText(
                        "Failure",
                        "Email was not sent successfully",
                        "error"
                      );
                    }
                  });
                }
              )
            }
          >
            Resend Receipt
          </Button>
        </Col>
      </Row>

      <Row>
        <Col lg="12" md="12">
          <Badge
            color={transaction.enabled ? "success" : "danger"}
            className="status-badge"
          >
            {transaction.enabled ? "Enabled" : "Disabled"}
          </Badge>
          <Badge
            color={transaction.activated ? "success" : "danger"}
            className="verification-badge"
          >
            {transaction.activated ? "Activated" : "Not Activated"}
          </Badge>
          {transaction.disputed && (
            <Badge color="warning" className="verification-badge">
              <span>Disputed - {transaction.disputestatus}</span>
            </Badge>
          )}
          {transaction.manual_override && (
            <Badge color="warning" className="verification-badge">
              <span>Manual Override</span>
            </Badge>
          )}
        </Col>
      </Row>

      <Row className="info-row">
        <Col lg="6" md="6">
          <label className="label">Transaction ID</label>
          <Input type="text" readOnly value={transaction.ctransid} />
        </Col>
        <Col lg="3" md="3">
          <label className="label">Code</label>
          <Input type="text" readOnly value={transaction.referenceid1} />
        </Col>
        <Col lg="3" md="3">
          <label className="label">Amount</label>
          <Input type="text" readOnly value={transaction.price} />
        </Col>
      </Row>

      <Row className="info-row">
        <Col lg="4" md="4">
          <label className="label">Gateway</label>
          <Input type="text" readOnly value={transaction.gateway} />
        </Col>
        <Col lg="4" md="4">
          <label className="label">Invoice #</label>
          <Input type="text" readOnly value={transaction.invoicenumber} />
        </Col>
        <Col lg="4" md="4">
          <label className="label">Storage Username</label>
          <Input type="text" readOnly value={transaction.storageusername} />
        </Col>
      </Row>

      <Row className="info-row">
        <Col lg="6" md="6">
          <label className="label">Created</label>
          <Input
            type="text"
            readOnly
            value={
              transaction.datetimecreated
                ? formatDate(transaction.datetimecreated)
                : ""
            }
          />
        </Col>
        <Col lg="6" md="6">
          <label className="label">Activation Expire</label>
          <Input
            type="text"
            readOnly
            value={
              transaction.datetimeexpire
                ? formatDate(transaction.datetimeexpire)
                : ""
            }
          />
        </Col>
      </Row>

      <Row className="info-row">
        <Col lg="6" md="6">
          <label className="label">Activated</label>
          <Input
            type="text"
            readOnly
            value={
              transaction.datetimeactivated
                ? formatDate(transaction.datetimeactivated)
                : ""
            }
          />
        </Col>
        <Col lg="6" md="6">
          <label className="label">Disabled</label>
          <Input
            type="text"
            readOnly
            value={
              transaction.datetimedisabled
                ? formatDate(transaction.datetimedisabled)
                : ""
            }
          />
        </Col>
      </Row>

      <Row className="info-row">
        <Col lg="6" md="6">
          <label className="label">Updated</label>
          <Input
            type="text"
            readOnly
            value={
              transaction.datetimeactivated
                ? formatDate(transaction.datetimeactivated)
                : ""
            }
          />
        </Col>
        <Col lg="6" md="6">
          <label className="label">Description</label>
          <Input type="text" readOnly value={transaction.shortdescription} />
        </Col>
      </Row>

      {transaction.disputed && (
        <>
          <hr className="divider" />

          <Row className="info-row">
            <Col lg="6" md="6">
              <label className="label">Date Disputed</label>
              <Input
                type="text"
                readOnly
                value={
                  transaction.datetimedisputed
                    ? formatDate(transaction.datetimedisputed)
                    : ""
                }
              />
            </Col>
            <Col lg="6" md="6">
              <label className="label">Initiated By</label>
              <Input
                type="text"
                readOnly
                value={transaction.disputeinitiatedby ?? ""}
              />
            </Col>
          </Row>

          <Row className="info-row">
            <Col lg="4" md="4">
              <label className="label">
                <span>Valid Dispute </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-pencil"
                  viewBox="0 0 16 16"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    updateDisputeValid({
                      variables: {
                        ctransid: transaction.ctransid,
                        disputevalid: !transaction.disputevalid,
                      },
                    }).then((res) => {
                      const status =
                        res?.data?.adminUpdateDisputeValidByCTransactionId
                          ?.status;

                      if (status) {
                        setTransaction((prevState) => {
                          return {
                            ...prevState,
                            disputevalid: !transaction.disputevalid,
                          };
                        });
                      }
                    });
                  }}
                >
                  <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325" />
                </svg>
              </label>
              <FormGroup className="d-block mt-2 ml-4 pb-2">
                <Input
                  type="checkbox"
                  checked={transaction.disputevalid}
                  readOnly
                  value={transaction.disputevalid ?? false}
                />
              </FormGroup>
            </Col>
            <Col lg="4" md="4">
              <label className="label">
                <span>Dispute Status </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-pencil"
                  viewBox="0 0 16 16"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    SADropdown(
                      "Update Dispute Status",
                      "Dispute Status",
                      "Confirm",
                      {
                        "Fighting Dispute": "Fighting Dispute",
                        "Paid Out": "Paid Out",
                        "Lost Dispute": "Lost Dispute",
                        "Won Dispute": "Won Dispute",
                      }
                    ).then((res) => {
                      updateDisputeStatus({
                        variables: {
                          ctransid: transaction.ctransid,
                          disputestatus: res,
                        },
                      }).then((res) => {
                        const status =
                          res?.data?.adminUpdateDisputeStatusByCTransactionId
                            ?.status;

                        if (status) {
                          setTransaction((prevState) => {
                            return {
                              ...prevState,
                              disputestatus:
                                res.data
                                  .adminUpdateDisputeStatusByCTransactionId
                                  .data,
                            };
                          });
                        }
                      });
                    });
                  }}
                >
                  <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325" />
                </svg>
              </label>
              <Input
                type="text"
                readOnly
                value={transaction.disputestatus ?? ""}
              />
            </Col>
          </Row>

          <Row className="info-row">
            <Col xs="12">
              <label className="label">Dispute Reason</label>
              <Input
                type="text"
                readOnly
                value={transaction.disputereason ?? ""}
              />
            </Col>
          </Row>

          <Row className="info-row">
            <Col xs="12">
              <label className="label">
                <span>Dispute Notes </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-pencil"
                  viewBox="0 0 16 16"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    SATextAreaInput(
                      "Update Dispute Notes",
                      "Dispute notes...",
                      transaction.disputenotes ?? ""
                    ).then((res) => {
                      updateDisputeNotes({
                        variables: {
                          ctransid: transaction.ctransid,
                          disputenotes: res,
                        },
                      }).then((res) => {
                        const status =
                          res?.data?.adminUpdateDisputeNotesByCTransactionId
                            ?.status;

                        if (status) {
                          setTransaction((prevState) => {
                            return {
                              ...prevState,
                              disputenotes:
                                res.data.adminUpdateDisputeNotesByCTransactionId
                                  .data,
                            };
                          });
                        }
                      });
                    });
                  }}
                >
                  <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325" />
                </svg>
              </label>
              <Input
                type="textarea"
                readOnly
                value={transaction.disputenotes ?? ""}
              />
            </Col>
          </Row>
        </>
      )}

      {transaction.manual_override && (
        <>
          <hr className="divider" />

          <Row className="info-row">
            <Col lg="6" md="6">
              <label className="label">Customer IP</label>
              <Input
                type="text"
                readOnly
                value={transaction.customer_ip ?? ""}
              />
            </Col>
            <Col lg="6" md="6">
              <label className="label">Override IP</label>
              <Input
                type="text"
                readOnly
                value={transaction.override_ip ?? ""}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default TransactionCard;
